import { Injectable, inject } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from './alert.service';
import { Router } from '@angular/router';
import { APP_ROUTES } from '../../config/app-routes.config';

@Injectable({
  providedIn: 'root',
})
export class RephraseTransferService {
  private translate = inject(TranslateService);
  private alertService = inject(AlertService);
  private router = inject(Router);

  private rephraseLanguages = new Set<string>();
  private pendingTransfer: { text: string; language: string } | null = null;
  private transferSubject = new Subject<{ text: string; language: string }>();

  public transfer$ = new Observable<{ text: string; language: string }>((observer) => {
    // If there is a pending transfer (set before navigation), deliver it once and clear it.
    if (this.pendingTransfer) {
      observer.next(this.pendingTransfer);
      this.pendingTransfer = null;
      observer.complete();
      return;
    }

    const sub = this.transferSubject.subscribe((payload) => {
      observer.next(payload);
      // Clear any pending transfer after delivery to avoid re-delivery to future subscribers
      this.pendingTransfer = null;
      // complete the observer so subscribers auto-unsubscribe
      observer.complete();
      sub.unsubscribe();
    });

    return () => sub.unsubscribe();
  });

  constructor() {
    this.translate.get('Text.RephraseLanguages').subscribe((res: Record<string, string>) => {
      if (res) {
        this.rephraseLanguages = new Set(Object.values(res));
      }
    });
  }

  public canRephrase(language: string | null | undefined): boolean {
    if (!language) {
      return false;
    }
    return this.rephraseLanguages.has(language);
  }

  public transferToRephrase(text: string, language: string) {
    if (!text || text.trim() === '') {
      return;
    }

    if (!this.canRephrase(language)) {
      const msg =
        this.translate.instant('Alert.RephraseLanguageNotAvailable') ??
        'Selected rephrase language is not available for rephrase.';
      this.alertService.showAlert('error', msg);
      return;
    }

    // store pending transfer so that a subscriber that is created after navigation
    // can still receive the payload exactly once
    this.pendingTransfer = { text, language };
    // emit to any existing subscribers
    this.transferSubject.next(this.pendingTransfer);
    this.router.navigate([APP_ROUTES.textRephrase]);
  }
}
